/**
 * Main container for RMC Order Intake Flow
 * Manages the transition between stepper and order management views
 */

import {trackEvent} from '@hconnect/common/logging/Analytics'
import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import React, {useState} from 'react'

import {RmcBulkOrderEdit} from '../components/bulkOrderEdit/RmcBulkOrderEdit'
import {RmcOrderPayload} from '../services/rmcOrderPlacementService'

import {RmcStepperContainer} from './RmcStepperContainer'

/**
 * Flow states for the RMC order intake process
 */
enum RmcFlowState {
  STEPPER = 'stepper',
  BULK_ORDER_EDIT = 'bulk_order_edit'
}

/**
 * Props for the RMC Order Intake Flow Container
 */
interface RmcOrderIntakeFlowContainerProps {
  /** Persisted order data for repeat order flow */
  persistedOrders?: RmcOrderPayload[]
}

/**
 * Main container for the complete RMC order intake flow
 * Handles navigation between stepper and order management
 */
export const RmcOrderIntakeFlowContainer: React.FC<RmcOrderIntakeFlowContainerProps> = ({
  persistedOrders
}) => {
  // Start with order management if we have persisted orders (repeat order flow)
  const [flowState, setFlowState] = useState<RmcFlowState>(
    persistedOrders && persistedOrders.length > 0
      ? RmcFlowState.BULK_ORDER_EDIT
      : RmcFlowState.STEPPER
  )
  const [completedOrderData, setCompletedOrderData] = useState<FieldsForm | null>(null)

  /**
   * Handle completion of the stepper - transition to order management
   */
  const handleStepperComplete = (formData: FieldsForm) => {
    // Track stepper completion event
    trackEvent('rmcStepperCompleted', {
      businessLine: 'RMC',
      materialId: formData.material?.materialId,
      quantity: formData.quantity
        ? formData.quantity.type === 'volume'
          ? formData.quantity.volume
          : formData.quantity.fullLoads
        : undefined,
      quantityUnit: formData.quantity?.quantityUom,
      deliveryDate:
        formData.dateTime && formData.dateTime[0]
          ? new Date(formData.dateTime[0]).toISOString().split('T')[0]
          : undefined
    })

    setCompletedOrderData(formData)
    setFlowState(RmcFlowState.BULK_ORDER_EDIT)
  }

  /**
   * Handle going back to stepper from bulk order edit
   */
  const handleBackToStepper = () => {
    setFlowState(RmcFlowState.STEPPER)
    // Keep the form data so user can continue editing
  }

  // Render based on current flow state
  switch (flowState) {
    case RmcFlowState.STEPPER:
      return <RmcStepperContainer onStepperComplete={handleStepperComplete} />

    case RmcFlowState.BULK_ORDER_EDIT:
      return (
        <RmcBulkOrderEdit
          initialOrderData={completedOrderData || undefined}
          persistedOrders={persistedOrders}
          onBackToStepper={handleBackToStepper}
        />
      )

    default:
      return <RmcStepperContainer onStepperComplete={handleStepperComplete} />
  }
}
